import { useMemo, useState } from 'react';
import { addDays, format, isSameDay, startOfWeek } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { useSimulationStore } from '@/state/simulationStore';
import { resolveDayOff } from '@/domain/artistAdmin';
import { bookingStatusTone, dayOffTileTone } from '@/utils/statusTone';
import { customerName } from '@/utils/format';
import { AWAITING_CLIENT } from '@/domain/selfBookFlow';
import type { Artist, Booking, CustomerRecord, SessionLength } from '@/domain/types';

interface Props {
  artist: Artist;
  /** This artist's bookings (any order — grouped + sorted per day here). */
  bookings: Booking[];
  customers: Record<string, CustomerRecord>;
  onOpen: (booking: Booking) => void;
}

// getDay() index (0=Sun..6=Sat) -> weekly-pattern key.
const DOW_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

const SESSION_LABEL: Record<SessionLength, string> = {
  small: 'Small',
  'half-day': 'Half day',
  'full-day': 'Full day',
  'multi-session': 'Multi-session',
};

const toMin = (t: string): number => {
  const [h, m] = t.split(':');
  return Number(h) * 60 + Number(m ?? 0);
};

/** Whole hours read cleaner than "4.0h"; halves keep the ".5" (e.g. 1.5h). */
const hoursLabel = (mins: number): string => {
  const h = mins / 60;
  return Number.isInteger(h) ? `${h}h` : `${h.toFixed(1)}h`;
};

const windowHours = (win: { startTime: string; endTime: string }): number =>
  Math.max(0, (toMin(win.endTime) - toMin(win.startTime)) / 60);

/** "13–19 Jul" when one month, "28 Jul–3 Aug" when the week straddles two. */
const rangeLabel = (start: Date, end: Date): string =>
  format(start, 'MMM') === format(end, 'MMM')
    ? `${format(start, 'd')}–${format(end, 'd MMM')}`
    : `${format(start, 'd MMM')}–${format(end, 'd MMM')}`;

/**
 * The artist's own week as a day-grouped agenda: Monday to Sunday, each day
 * showing its rostered window and every booking on it (start time, client,
 * session length, brief, deposit/lifecycle status). Day-off and empty days read
 * at a glance. A pager steps between weeks. Replaces the old fixed-slot
 * "Calendar" tab, which showed neither names nor real times.
 */
export default function ArtistWeekSchedule({ artist, bookings, customers, onOpen }: Props) {
  const clockIso = useSimulationStore((s) => s.clockIso);
  const [weekOffset, setWeekOffset] = useState(0);

  const now = useMemo(() => new Date(clockIso), [clockIso]);
  const weekStart = useMemo(
    () => addDays(startOfWeek(now, { weekStartsOn: 1 }), weekOffset * 7),
    [now, weekOffset],
  );
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)).map((day) => ({
        day,
        off: resolveDayOff(artist.availability, day),
        window: artist.availability.pattern[DOW_KEYS[day.getDay()]!],
        dayBookings: bookings
          .filter((b) => isSameDay(new Date(b.start), day))
          .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()),
        isToday: isSameDay(day, now),
      })),
    [artist, bookings, weekStart, now],
  );

  const totalBooked = days.reduce((n, d) => n + d.dayBookings.length, 0);
  const chairHours = days.reduce(
    (h, d) => h + d.dayBookings.reduce((m, b) => m + b.durationMin, 0) / 60,
    0,
  );

  return (
    <div className="max-w-3xl" data-testid="artist-week-schedule">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div>
          <h3 className="heading-serif text-base text-cream">
            {weekOffset === 0 ? 'This week' : rangeLabel(weekStart, weekEnd)}
          </h3>
          <p className="font-inter text-[11px] uppercase tracking-widest text-cream-faint mt-1">
            {totalBooked} booked · {hoursLabel(chairHours * 60)} in the chair
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            data-testid="week-prev"
            onClick={() => setWeekOffset((o) => o - 1)}
            aria-label="Previous week"
            className="h-8 w-8 rounded-full border border-gold-dim/40 text-cream-soft inline-flex items-center justify-center hover:text-gold-bright hover:border-gold-bright transition"
          >
            <ChevronLeft size={15} />
          </button>
          <span
            data-testid="week-range"
            className="font-mono text-sm text-cream tabular-nums px-2 min-w-[7rem] text-center"
          >
            {rangeLabel(weekStart, weekEnd)}
          </span>
          <button
            type="button"
            data-testid="week-next"
            onClick={() => setWeekOffset((o) => o + 1)}
            aria-label="Next week"
            className="h-8 w-8 rounded-full border border-gold-dim/40 text-cream-soft inline-flex items-center justify-center hover:text-gold-bright hover:border-gold-bright transition"
          >
            <ChevronRight size={15} />
          </button>
          {weekOffset !== 0 ? (
            <button
              type="button"
              data-testid="week-this"
              onClick={() => setWeekOffset(0)}
              className="ml-1 rounded-full border border-gold-dim/40 px-3 py-1.5 font-inter text-[10px] uppercase tracking-widest text-cream-soft hover:text-gold-bright transition"
            >
              This week
            </button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {days.map(({ day, off, window, dayBookings, isToday }) => (
          <section
            key={day.toISOString()}
            data-testid={`week-day-${format(day, 'yyyy-MM-dd')}`}
            className={clsx(
              'rounded-xl border overflow-hidden',
              isToday ? 'border-gold-bright/60' : 'border-gold-dim/20',
              off ? 'bg-ink-dark' : 'bg-ink-deep',
            )}
          >
            <div
              className={clsx(
                'flex items-center justify-between gap-4 px-5 py-3 border-b',
                off ? 'border-transparent' : 'border-gold-dim/15',
                isToday && 'bg-gold/5',
              )}
            >
              <div className="flex items-baseline gap-3">
                <span className="heading-serif text-sm text-cream">
                  {format(day, 'EEE')}{' '}
                  <span className="text-cream-soft font-normal">{format(day, 'd MMM')}</span>
                </span>
                {isToday ? (
                  <span className="rounded-full bg-gold-bright px-2 py-0.5 font-inter text-[9px] font-bold uppercase tracking-widest text-ink-black">
                    Today
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-3">
                {off ? (
                  <span className="pill-blood text-[10px]">{dayOffTileTone(off.kind).label}</span>
                ) : (
                  <>
                    <span
                      className={clsx(
                        'font-inter text-[11px] uppercase tracking-widest',
                        dayBookings.length ? 'text-gold' : 'text-cream-faint',
                      )}
                    >
                      {dayBookings.length} booked
                    </span>
                    {window ? (
                      <span className="font-mono text-sm text-cream-soft tabular-nums">
                        {window.startTime}
                        {'–'}
                        {window.endTime}
                      </span>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            {off ? (
              <p className="px-5 py-4 font-serif text-sm italic text-cream-faint">
                Day off{off.note ? ` · ${off.note}` : ''}.
              </p>
            ) : dayBookings.length === 0 ? (
              <div className="px-5 py-4 flex items-center justify-between gap-3">
                <span className="font-serif text-sm text-cream-faint">No bookings yet.</span>
                {window ? (
                  <span className="font-inter text-[11px] uppercase tracking-widest text-cream-faint">
                    {windowHours(window)}h open · shareable via self-book
                  </span>
                ) : null}
              </div>
            ) : (
              <ol>
                {dayBookings.map((b) => {
                  const cust = customers[b.customerId];
                  const tone = bookingStatusTone(b.status);
                  const start = new Date(b.start);
                  return (
                    <li key={b.id}>
                      <button
                        type="button"
                        data-testid={`week-booking-${b.id}`}
                        onClick={() => onOpen(b)}
                        className="w-full text-left flex items-start gap-4 px-5 py-3.5 border-t border-gold-dim/10 first:border-t-0 hover:bg-ink-black transition"
                      >
                        <div className="w-[68px] flex-none">
                          <div className="font-mono text-[17px] text-cream tabular-nums leading-none">
                            {format(start, 'HH:mm')}
                          </div>
                          <div className="font-inter text-[10px] uppercase tracking-wide text-cream-faint mt-1.5">
                            {hoursLabel(b.durationMin)}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2.5 flex-wrap">
                            <span className="font-inter text-base font-semibold text-cream">
                              {cust
                                ? customerName(cust)
                                : b.customerId === AWAITING_CLIENT
                                  ? 'Awaiting client'
                                  : b.customerId}
                            </span>
                            <span className="rounded-full border border-gold-dim/60 bg-gold/5 px-2 py-0.5 font-inter text-[10px] font-semibold uppercase tracking-wider text-gold">
                              {SESSION_LABEL[b.sessionLength]}
                            </span>
                          </div>
                          {b.designBrief ? (
                            <p className="font-serif text-sm text-cream-soft mt-1.5 truncate">
                              {b.designBrief}
                            </p>
                          ) : null}
                          {b.referencePhotos.length > 0 ? (
                            <p className="font-inter text-[10px] uppercase tracking-wide text-cream-faint mt-1.5">
                              {b.referencePhotos.length} reference
                              {b.referencePhotos.length === 1 ? '' : 's'}
                            </p>
                          ) : null}
                        </div>
                        <span
                          className={clsx(
                            'flex-none inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-inter text-[10px] font-semibold uppercase tracking-wider',
                            tone.border,
                            tone.text,
                          )}
                        >
                          <span className={clsx('h-1.5 w-1.5 rounded-full', tone.dot)} />
                          {tone.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
